import * as React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import { getOrders } from '../queries/getOrders';
import { handleError } from '../../../../shared/ErrorCatching';
import { Paper } from '@mui/material';
import OrderListLoading from './OrderListLoading';
import { Region, Order } from '../BookingsCalendarTypes';
import { styled } from '@mui/material/styles';
import OrderListFilters from './OrderListFilters';
import OrderListBody from './OrderListBody';
import useFilterOrders from '../state/useFilterOrders';
import UnbookedOrdersPageHeading from '../UnbookedOrdersPageV2Heading';
import { useMutation, useQueryClient } from 'react-query';
import { OrderListApi } from '../components/OrderListComponents/queries/OrderListApi';
import { FilterHiddenOrderCheckbox } from './FilterHiddenOrderCheckbox';
import { Translate } from '../../../../shared/LanguageService';
import { useTelemetryContext } from '../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../telemetry/utils';

type ExternalProps = {
    user: any;
    onSelectOrder: (selectedOrder: Order, availableRegions: Region[]) => void;
};

type OrderListSectionLabels = {
    filterHiddenOrders: string;
};

type OrderListSectionProps = {
    availableRegions: Region[];
    orders: Order[];
    onSelectOrder: (selectedOrder: Order, availableRegions: Region[]) => void;
    onSubmitSplitOrder: (formData: FormData) => Promise<void>;
    labels: OrderListSectionLabels;
    onClickHideOrders: (orderIds: number[]) => Promise<void>;
    onUnHideOrders: (orderIds: number[]) => Promise<void>;
};

type ApiResponse = {
    data: any;
    isLoading: boolean;
    isError: boolean;
    error: any;
};

const StyledOrderListGrid = styled(Grid)(() => ({
    paddingTop: '0px !important',
    paddingLeft: '0px !important'
}));

function OrderListSection({
    availableRegions,
    orders,
    onSelectOrder,
    onSubmitSplitOrder,
    onClickHideOrders,
    onUnHideOrders,
    labels
}: OrderListSectionProps) {
    let [showHiddenOrders, setShowHiddenOrders] = React.useState(false);
    let numberOfOrders = orders.length;

    const handleSelectOrder = (selectedOrder) => {
        onSelectOrder(selectedOrder, availableRegions);
    };

    const {
        filteredOrders,
        searchQuery,
        selectedRegion,
        handleChangeRegionFilter,
        handleChangeSearchQuery
    } = useFilterOrders(orders);

    return (
        <StyledOrderListGrid item xs={12} md={4} aria-label="order-list-section">
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    paddingBottom: '10px'
                }}
            >
                <UnbookedOrdersPageHeading numberOfOrders={numberOfOrders} />
                <Box sx={{ flexGrow: 1 }} />
                <FilterHiddenOrderCheckbox
                    isChecked={showHiddenOrders}
                    onChange={() => setShowHiddenOrders((s) => !s)}
                    label={labels.filterHiddenOrders}
                />
            </Box>
            <Paper
                variant="outlined"
                sx={{
                    minHeight: '77vh'
                }}
            >
                <OrderListFilters
                    searchQuery={searchQuery}
                    selectedRegion={selectedRegion}
                    availableRegions={availableRegions}
                    onChangeRegion={handleChangeRegionFilter}
                    onSearchQuery={handleChangeSearchQuery}
                />

                <Divider />
                <OrderListBody
                    orders={filteredOrders}
                    onSelectedOrder={handleSelectOrder}
                    onSubmitSplitOrder={onSubmitSplitOrder}
                    onClickHideOrders={onClickHideOrders}
                    showHiddenOrders={showHiddenOrders}
                    onUnHideOrders={onUnHideOrders}
                />
            </Paper>
        </StyledOrderListGrid>
    );
}

let OrderListSectionTranslationLayer = (props: {
    onSelectOrder: (selectedOrder: Order, availableRegions: Region[]) => void;
    availableRegions: Region[];
    orders: Order[];
    onSubmitSplitOrder: (formData: FormData) => Promise<void>;
    onClickHideOrders: (orderIds: number[]) => Promise<void>;
    onUnHideOrders: (orderIds: number[]) => Promise<void>;
}) => {
    let labels: OrderListSectionLabels = {
        filterHiddenOrders: Translate('orders/list/bookings-calendar/filter/show-hidden-orders')
    };

    return <OrderListSection {...props} labels={labels} />;
};

const OrderListSectionDataLayer = ({ user, ...props }: ExternalProps) => {
    const { data, isLoading, isError, error }: ApiResponse = getOrders();
    let queryClient = useQueryClient();
    let { logger } = useTelemetryContext();

    let splitOrderMutation = useMutation(
        ({ formData }: { formData: FormData }) => OrderListApi.splitOrder(formData),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('unbookedOrders');
            }
        }
    );

    let hideOrdersMutation = useMutation(
        ({ orderIds }: { orderIds: number[] }) => OrderListApi.hideOrders(orderIds),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('unbookedOrders');
            }
        }
    );

    let unHideOrdersMutation = useMutation(
        ({ orderIds }: { orderIds: number[] }) => OrderListApi.unHideOrders(orderIds),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('unbookedOrders');
            }
        }
    );

    if (isError) {
        handleError(error);
    }

    if (isLoading) {
        return <OrderListLoading />;
    }

    let { regions, unBookedOrders } = data;

    async function handleSubmitSplitOrder(formData: FormData): Promise<void> {
        return splitOrderMutation.mutateAsync({ formData }).catch((error) => {
            let additionalInfo = {
                origin: 'OrderListSection.handleSubmitSplitOrder',
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInfo);
        });
    }

    async function handleHideOrders(orderIds: number[]): Promise<void> {
        return hideOrdersMutation.mutateAsync({ orderIds }).catch((error) => {
            let additionalInfo = {
                origin: 'OrderListSection.handleHideOrders',
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInfo);
        });
    }

    async function handleUnHideOrders(orderIds: number[]): Promise<void> {
        return unHideOrdersMutation.mutateAsync({ orderIds }).catch((error) => {
            let additionalInfo = {
                origin: 'OrderListSection.handleUnHideOrders',
                userInfo: getUserInfo(user)
            };
            logger.logException(error, additionalInfo);
        });
    }

    return (
        <OrderListSectionTranslationLayer
            availableRegions={regions}
            orders={unBookedOrders}
            onSubmitSplitOrder={handleSubmitSplitOrder}
            onClickHideOrders={handleHideOrders}
            onUnHideOrders={handleUnHideOrders}
            {...props}
        />
    );
};

export default OrderListSectionDataLayer;
